// Monitor — 真實資料版（Route 4, PR #94）。
//
// - KPI 取自 GET /v1/dashboard/overview（同 Overview）
// - Site health grid 取自 overview.watch_sites（前 6）
// - Incident board 取自 GET /v1/incidents?severity&status&from&to (cross-partition)
// - 30s 自動 refresh（可暫停）+ 手動重新整理 + 靜音 toggle
// - 兼容 super_admin acting tenant

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  RefreshCw,
  Filter,
  Activity as ActivityIcon,
  Volume2,
  VolumeX,
  ServerCrash,
  Building2,
  Inbox,
  Pause,
  Play,
} from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import MetricCard from "../components/MetricCard.js";
import IncidentTable from "../components/IncidentTable.js";
import SiteHealthCard from "../components/SiteHealthCard.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import {
  fetchOverview,
  friendlyDashboardError,
  type OverviewResponse,
} from "../lib/dashboardApi.js";
import {
  listIncidents,
  friendlyIncidentsError,
  type IncidentRow,
  type ApiSeverity,
  type IncidentStatus,
} from "../lib/incidentsApi.js";
import type { Severity, Site, Incident } from "../types/dashboard.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";

const SEVERITIES: Array<{ key: ApiSeverity | "all"; label: string }> = [
  { key: "all", label: "全部" },
  { key: "critical", label: "嚴重" },
  { key: "high", label: "高" },
  { key: "med", label: "中" },
  { key: "low", label: "低" },
  { key: "info", label: "提示" },
];
const STATUSES: Array<{ key: IncidentStatus | "all"; label: string }> = [
  { key: "all", label: "全部" },
  { key: "open", label: "開啟中" },
  { key: "ack", label: "已確認" },
  { key: "resolved", label: "已處理" },
];

const REFRESH_MS = 30_000;

function severityToUi(s: ApiSeverity): Severity {
  return s === "med" ? "medium" : s;
}

function relativeFromIso(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec} 秒前`;
  if (sec < 3600) return `${Math.floor(sec / 60)} 分鐘前`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} 小時前`;
  return `${Math.floor(sec / 86400)} 天前`;
}

function watchSiteToUi(s: OverviewResponse["watch_sites"][number]): Site {
  return {
    id: s.id,
    name: s.name,
    client: s.client,
    health: s.health,
    completion: s.completion,
    activeOfficers: s.active_officers,
    lastTap: relativeFromIso(s.last_tap),
  };
}

function incidentToUi(i: IncidentRow): Incident {
  return {
    id: i.id,
    ts: i.created_at,
    site: i.site_name ?? i.site_id,
    location: "—",
    severity: severityToUi(i.severity),
    status: i.status,
    message: i.title || i.body,
  };
}

type Stage = "loading" | "ready" | "error";

export default function Monitor(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;

  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [overviewStage, setOverviewStage] = useState<Stage>("loading");
  const [overviewErr, setOverviewErr] = useState<string | null>(null);

  const [incidents, setIncidents] = useState<IncidentRow[]>([]);
  const [incidentsStage, setIncidentsStage] = useState<Stage>("loading");
  const [incidentsErr, setIncidentsErr] = useState<string | null>(null);

  const [sev, setSev] = useState<ApiSeverity | "all">("all");
  const [status, setStatus] = useState<IncidentStatus | "all">("all");
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(false);
  const lastIncidentSig = useRef<string | null>(null);

  const loadOverview = useCallback(async () => {
    setOverviewStage((s) => (s === "ready" ? s : "loading"));
    try {
      const data = await fetchOverview({ tenantId: tenantParam });
      setOverview(data);
      setOverviewStage("ready");
      setOverviewErr(null);
    } catch (err) {
      setOverviewErr(friendlyDashboardError(err));
      setOverviewStage("error");
    }
  }, [tenantParam]);

  const loadIncidents = useCallback(async () => {
    setIncidentsStage((s) => (s === "ready" ? s : "loading"));
    try {
      const r = await listIncidents({
        tenantId: tenantParam,
        severity: sev === "all" ? "" : sev,
        status: status === "all" ? "" : status,
        limit: 100,
      });
      setIncidents(r.items);
      setIncidentsStage("ready");
      setIncidentsErr(null);

      // 提示音：偵測「比上次第一筆更新」就 beep（除非 muted）
      const sig = r.items[0]?.id ?? null;
      if (sig && lastIncidentSig.current && sig !== lastIncidentSig.current && !muted) {
        try {
          const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.connect(g);
          g.connect(ctx.destination);
          o.frequency.value = 880;
          g.gain.value = 0.05;
          o.start();
          setTimeout(() => { o.stop(); ctx.close(); }, 120);
        } catch {
          // ignore audio failure
        }
      }
      lastIncidentSig.current = sig;
    } catch (err) {
      setIncidentsErr(friendlyIncidentsError(err));
      setIncidentsStage("error");
    }
  }, [tenantParam, sev, status, muted]);

  const reload = useCallback(() => {
    void loadOverview();
    void loadIncidents();
  }, [loadOverview, loadIncidents]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(reload, REFRESH_MS);
    return () => clearInterval(id);
  }, [paused, reload]);

  if (session.super_admin && !tenantParam) {
    return (
      <>
        <PageHeader title="即時監控" description="案場狀態、incident board、最新感應紀錄" />
        <TenantPickerEmptyState />
      </>
    );
  }

  const watchSites = (overview?.watch_sites ?? []).map(watchSiteToUi);
  const uiIncidents = incidents.map(incidentToUi);
  const openCount = incidents.filter((i) => i.status === "open").length;

  return (
    <>
      <PageHeader
        title="即時監控"
        description="案場狀態、incident board、最新感應紀錄"
        actions={
          <>
            <button
              onClick={() => setMuted((v) => !v)}
              className="btn-secondary text-sm"
              aria-label={muted ? "開啟提示音" : "靜音"}
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              {muted ? "靜音中" : "提示音"}
            </button>
            <button onClick={() => setPaused((v) => !v)} className="btn-secondary text-sm">
              {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              {paused ? "繼續自動更新" : "暫停自動更新"}
            </button>
            <button className="btn-primary text-sm" onClick={reload} disabled={overviewStage === "loading" || incidentsStage === "loading"}>
              <RefreshCw className={`h-4 w-4 ${(overviewStage === "loading" || incidentsStage === "loading") ? "animate-spin" : ""}`} /> 重新整理
            </button>
          </>
        }
      />

      {/* KPI strip */}
      {overviewStage === "loading" && !overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="card p-4">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="mt-3 h-8 w-32" />
            </div>
          ))}
        </div>
      )}
      {overviewStage === "error" && (
        <EmptyState
          icon={ServerCrash}
          title="KPI 載入失敗"
          description={overviewErr ?? ""}
          action={<button className="btn-primary text-sm" onClick={() => void loadOverview()}>再試一次</button>}
        />
      )}
      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard label={overview.kpi.completion.label} value={overview.kpi.completion.value} suffix={overview.kpi.completion.suffix} trend={overview.kpi.completion.trend} icon={ActivityIcon} />
          <MetricCard label={overview.kpi.missed.label}     value={overview.kpi.missed.value}     suffix={overview.kpi.missed.suffix}     trend={overview.kpi.missed.trend} trendInverse />
          <MetricCard label="開啟中事件" value={openCount} suffix=" 件" />
          <MetricCard label={overview.kpi.officers.label}   value={overview.kpi.officers.value}   suffix={overview.kpi.officers.suffix}   trend={overview.kpi.officers.trend} />
        </div>
      )}

      {/* Site health board */}
      <div className="mt-6">
        <h2 className="mb-3 text-base font-semibold text-text-primary">案場狀態</h2>
        {overviewStage === "loading" && watchSites.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (<div key={i} className="card p-4 space-y-3"><Skeleton className="h-4 w-1/2" /><Skeleton className="h-3 w-1/3" /><Skeleton className="h-2 w-full" /></div>))}
          </div>
        ) : watchSites.length === 0 ? (
          <EmptyState icon={Building2} title="目前沒有需要關注的案場" description="所有案場運行正常，繼續保持。" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
            {watchSites.map((s) => (<SiteHealthCard key={s.id} site={s} />))}
          </div>
        )}
      </div>

      {/* Incident board */}
      <div className="mt-6">
        <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-base font-semibold text-text-primary">事件清單</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-3.5 w-3.5 text-text-muted" />
            <FilterChips value={sev} options={SEVERITIES} onChange={(k) => setSev(k as ApiSeverity | "all")} />
            <span className="hidden sm:inline w-px h-4 bg-border" />
            <FilterChips value={status} options={STATUSES} onChange={(k) => setStatus(k as IncidentStatus | "all")} />
          </div>
        </div>
        {incidentsStage === "error" && (
          <EmptyState
            icon={ServerCrash}
            title="事件清單載入失敗"
            description={incidentsErr ?? ""}
            action={<button className="btn-primary text-sm" onClick={() => void loadIncidents()}>再試一次</button>}
          />
        )}
        {incidentsStage !== "error" && (
          <div className="card overflow-hidden">
            {incidentsStage === "loading" && incidents.length === 0 ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex gap-3"><Skeleton className="h-4 w-12" /><Skeleton className="h-4 w-1/3" /><Skeleton className="h-4 w-1/4" /></div>
                ))}
              </div>
            ) : uiIncidents.length === 0 ? (
              <EmptyState icon={Inbox} title="目前沒有符合條件的事件" description="調整篩選條件，或等待新的事件回報。" />
            ) : (
              <IncidentTable incidents={uiIncidents} />
            )}
          </div>
        )}
      </div>
    </>
  );
}

function FilterChips<T extends string>({
  value, options, onChange,
}: {
  value: T;
  options: { key: T; label: string }[];
  onChange: (k: T) => void;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={`text-xs rounded-md px-2 py-1 border transition-colors ${
            value === o.key
              ? "bg-accent-subtle border-accent/40 text-accent"
              : "bg-bg-surface border-border text-text-secondary hover:bg-bg-hover hover:text-text-primary"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
